import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { BurgerMenu, Close, LogoWordmark } from "@/assets/icons/icons";
import { LanguageMenu } from "@/components/Layout/BaseLayouts/Sidebar/Navigation/LanguageMenu";
import { NavPanelContent } from "@/components/Layout/BaseLayouts/Sidebar/Navigation/NavPanelContent";

/**
 * Burger menu button + slide-in drawer for mobile/tablet.
 * `floating` mirrors Header's atTop state to keep the portaled toggle button aligned with it.
 */
export const BurgerDrawer = ({ floating }: { floating?: boolean }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Close the drawer if resized past the desktop breakpoint (button is
  // desktop:hidden, so it can't be toggled shut). Keep this in sync with the
  // `desktop` breakpoint below (1024px) — a mismatch here strands the drawer
  // open with no visible way to close it.
  useEffect(() => {
    if (!open) {
      return;
    }

    const query = window.matchMedia("(min-width: 1024px)");
    if (query.matches) {
      close();

      return;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        close();
      }
    };
    query.addEventListener("change", handleChange);

    return () => query.removeEventListener("change", handleChange);
  }, [open]);

  return (
    <>
      {/* Portaled to body -- Dialog inerts the main tree while open, which would swallow taps on this button otherwise.
          Only rendered while closed: once open, the panel's own header close-button is the single close affordance (per design). */}
      {!open &&
        createPortal(
          <button
            type="button"
            aria-label={t("layout.menu.burger.open")}
            onClick={() => setOpen(true)}
            className={clsx(
              "fixed z-50 flex h-13 items-center justify-center rounded-md p-2 transition-[top,right] duration-300 ease-in-out hover:bg-surface-tertiary desktop:hidden",
              floating ? "right-6 top-2" : "right-4 top-0",
            )}
          >
            <BurgerMenu className="size-6 [&>path]:fill-foreground-primary" />
          </button>,
          document.body,
        )}

      <Transition show={open}>
        <Dialog onClose={close}>
          <Transition.Child
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 z-40 bg-surface-primary/40"
              aria-hidden="true"
            />
          </Transition.Child>

          {/* Positioning classes go on this transform-animated element, not a nested Dialog.Panel -- see LanguageMenu.tsx for the same containing-block issue. */}
          <Transition.Child
            as="div"
            className="fixed inset-y-0 right-0 z-40 flex w-full tablet:w-[calc(100%-32px)] tablet:max-w-sm"
            enter="ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel
              data-testid="burger-drawer-panel"
              className="flex w-full flex-col overflow-y-auto tablet:p-2"
            >
              <div className="flex flex-1 flex-col gap-gutter bg-background-secondary p-5 tablet:rounded-lg">
                <div className="flex items-center gap-2">
                  <LogoWordmark
                    className="h-8 w-16 text-foreground-primary"
                    aria-label={t("Common.logoName")}
                  />
                  <button
                    type="button"
                    aria-label={t("layout.menu.burger.close")}
                    onClick={close}
                    className="ml-auto flex items-center justify-center rounded-md p-2 hover:bg-surface-tertiary"
                  >
                    <Close className="size-6 [&>path]:fill-foreground-primary" />
                  </button>
                </div>

                <NavPanelContent onNavigate={close} />

                <div className="mt-auto">
                  <LanguageMenu />
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
};
